import { createApp, h, inject } from "vue";
import { createInertiaApp, Link, usePage } from "@inertiajs/vue3";
import { vue3Debounce } from "vue-debounce";
import { createI18n } from "vue-i18n";
import axios from "axios";
import Vue3Lottie from "vue3-lottie";
import VueQrcodeReader from "vue3-qrcode-reader";
import { BrowserTracing } from "@sentry/tracing";
import * as Sentry from "@sentry/vue";
import VueHotjar from "vue-hotjar-next";
import { useWebView } from "@/Tailwind/Mixins/useWebView";
import { useDevice } from "@/Tailwind/Mixins/useDevice";
import mitt from "mitt";
import { createPinia } from "pinia";
import { directionStore } from "@/Stores/direction";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import permissions from "@/Tailwind/Plugins/permissions";
import { setupCalendar } from "v-calendar";
import "v-calendar/style.css";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import breakpoints from "@/Plugins/breakpoints";
import { Echo, events } from "@/events";
import { initializePendo } from "@/pendo";
import InlineSvg from "vue-inline-svg";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
window.axios = axios;

const { environment, version } = window.application;

const emitter = mitt();
const pinia = createPinia();

pinia.use(piniaPluginPersistedstate);

// https://tanstack.com/query/v5/docs/framework/vue/reference/useQuery
const vueQueryPluginOptions = {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                // Globally disable automatic refetching to force
                // individual queries to determine whether it is needed.
                refetchInterval: false,
                refetchIntervalInBackground: false,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
            },
        },
    },
};

const queryClient = new QueryClient({
    ...vueQueryPluginOptions.queryClientConfig,
});

createInertiaApp({
    resolve: (name) => {
        if (name.startsWith("Tailwind::")) {
            return require(`./Tailwind/${name.split("::")[1]}`).default;
        }
        return require(`./Pages/${name}`).default;
    },
    setup({ el, App, props, plugin }) {
        const i18n = createI18n({
            legacy: false,
            locale: props.initialPage.props.locale,
            fallbackLocale: "en",
            messages: {
                en: window.translations.en || {},
                nl: window.translations.nl || {},
                fr: window.translations.fr || {},
                es: window.translations.es || {},
                de: window.translations.de || {},
                bg: window.translations.bg || {},
                zh: window.translations.zh || {},
                cs: window.translations.cs || {},
                da: window.translations.da || {},
                et: window.translations.et || {},
                fi: window.translations.fi || {},
                el: window.translations.el || {},
                hu: window.translations.hu || {},
                id: window.translations.id || {},
                it: window.translations.it || {},
                ja: window.translations.ja || {},
                lv: window.translations.lv || {},
                lt: window.translations.lt || {},
                pl: window.translations.pl || {},
                ro: window.translations.ro || {},
                ru: window.translations.ru || {},
                sk: window.translations.sk || {},
                sl: window.translations.sl || {},
                sv: window.translations.sv || {},
                tr: window.translations.tr || {},
                pt: window.translations.pt || {},
                uk: window.translations.uk || {},
            },
        });

        const direction =
            props.initialPage.props.locale === "ar" ? "rtl" : "ltr";

        const { updateDevice } = useDevice();
        const { runningInWebView, getWebViewBridge } = useWebView();

        const onMessagingDetailsChanged = (event) => {
            const auth = usePage().props.auth;

            if (!auth) {
                return;
            }

            const { deviceId, deviceToken, permission } = event.detail;
            updateDevice(deviceId, deviceToken, permission);
        };

        const app = createApp({
            render: () => h(App, props),
            created: () => {
                directionStore().setDirection(direction);
            },
            mounted: () => {
                const echo = inject("echo");
                const emitter = inject("emitter");

                emitter.on("authenticated", () => {
                    const user = usePage().props?.auth?.user;
                    if (!user) {
                        throw new Error(
                            "Failed to retrieve user from page props."
                        );
                    }

                    initializePendo(user);

                    const userId = user.id;
                    const channel = echo.private(`users.${userId}`);

                    events.forEach((event) => {
                        channel.listen(`.${event}`, (payload) =>
                            emitter.emit(event, payload)
                        );
                    });
                });

                if (runningInWebView()) {
                    getWebViewBridge().addEventListener(
                        "onMessagingDetailsChanged",
                        onMessagingDetailsChanged
                    );
                    getWebViewBridge().finishedLoading();
                }

                const auth = usePage().props.auth;
                const userId = auth?.user?.id;
                if (!userId) {
                    return;
                }

                emitter.emit("authenticated", userId);
            },
            destroyed: () => {
                if (runningInWebView()) {
                    getWebViewBridge().removeEventListener(
                        "onMessagingDetailsChanged",
                        onMessagingDetailsChanged
                    );
                }
            },
        })
            .use(plugin)
            .use(permissions)
            .use(i18n)
            .use(Vue3Lottie)
            .use(VueQrcodeReader)
            .use(VueHotjar, {
                id: Number(process.env.MIX_HOTJAR_SITE_ID),
            })
            .component("InertiaLink", Link)
            .component("inline-svg", InlineSvg)
            .directive("debounce", vue3Debounce({ lock: true }))
            .use(pinia)
            .use(setupCalendar, {})
            .use(VueQueryPlugin, { queryClient })
            .use(breakpoints);

        Sentry.init({
            app,
            dsn: process.env.MIX_SENTRY_VUE_DSN,
            integrations: [new BrowserTracing()],
            tracesSampleRate: parseFloat(
                process.env.MIX_SENTRY_TRACES_SAMPLE_RATE
            ),
            logErrors: true,
            environment: environment,
            release: version,
            ignoreErrors: ["Non-Error promise rejection captured"],
        });

        app.provide("emitter", emitter);
        app.provide("echo", Echo);

        app.mount(el);
    },
});
